import { api } from "./client";
import type { TagRef } from "./types";

const base = (slug: string) => `/v1/c/${slug}`;

export interface TagRow extends TagRef {
  appliesTo: "contact" | "conversation" | "both";
  isActive: boolean;
  contacts: number;
  conversations: number;
}
export interface SnippetRow {
  id: string;
  teamId: string | null;
  title: string;
  shortcut: string | null;
  body: string;
  variables: string[];
  isActive: boolean;
  updatedAt: string;
}
export interface ContactListRow {
  id: string;
  displayName: string;
  address: string | null;
  channelKind: string | null;
  source: string;
  externalRef: string | null;
  fields: Record<string, unknown>;
  isBlocked: boolean;
  lastActivityAt: string | null;
  createdAt: string;
  tags?: TagRef[];
}
export interface ImportRow {
  phone: string;
  name?: string;
  fields?: Record<string, unknown>;
  tags?: string[];
  externalRef?: string;
  marketingOptIn?: boolean;
}
export interface ImportResult {
  created: number;
  updated: number;
  errors: Array<{ row: number; reason: string }>;
}

export type OutcomeCategory = "success" | "neutral" | "negative" | "invalid";
export interface OutcomeRow {
  id: string;
  code: string;
  label: Record<string, string> | string;
  category: OutcomeCategory;
  isSuccess: boolean;
  requiresNote: boolean;
  reopenAllowed: boolean;
  sort: number;
  isActive: boolean;
  used: number;
  /** One of DoneTalk's defaults, not the company's own. */
  preset?: boolean;
}
export const outcomesApi = {
  list: (slug: string) =>
    api<{ outcomes: OutcomeRow[] }>(`${base(slug)}/settings/outcomes`),
  create: (
    slug: string,
    body: {
      label: string;
      category: OutcomeCategory;
      requiresNote?: boolean;
      reopenAllowed?: boolean;
    },
  ) =>
    api<{ outcome: OutcomeRow }>(`${base(slug)}/outcomes`, {
      method: "POST",
      body,
    }),
  patch: (
    slug: string,
    id: string,
    body: Partial<{
      label: string;
      category: OutcomeCategory;
      requiresNote: boolean;
      reopenAllowed: boolean;
      isActive: boolean;
    }>,
  ) => api(`${base(slug)}/outcomes/${id}`, { method: "PATCH", body }),
  reorder: (slug: string, ids: string[]) =>
    api(`${base(slug)}/outcomes/order`, { method: "PUT", body: { ids } }),
};

export const tagsApi = {
  list: (slug: string) => api<{ tags: TagRow[] }>(`${base(slug)}/tags`),
  create: (
    slug: string,
    body: {
      name: string;
      color?: string;
      appliesTo?: TagRow["appliesTo"];
      /** People who get the tag at once (WhatsApp's "conversations included"). */
      contactIds?: string[];
    },
  ) => api<{ tag: TagRow }>(`${base(slug)}/tags`, { method: "POST", body }),
  patch: (
    slug: string,
    id: string,
    body: Partial<{
      name: string;
      color: string;
      appliesTo: TagRow["appliesTo"];
      isActive: boolean;
    }>,
  ) => api(`${base(slug)}/tags/${id}`, { method: "PATCH", body }),
  tagContact: (slug: string, contactId: string, tagId: string, on: boolean) =>
    api(`${base(slug)}/contacts/${contactId}/tags/${tagId}`, {
      method: on ? "PUT" : "DELETE",
    }),
};
export const snippetsApi = {
  list: (slug: string, all = false) =>
    api<{ snippets: SnippetRow[] }>(`${base(slug)}/snippets`, {
      query: all ? { all: 1 } : {},
    }),
  create: (
    slug: string,
    body: {
      title: string;
      shortcut?: string;
      body: string;
      teamId?: string | null;
    },
  ) =>
    api<{ snippet: SnippetRow }>(`${base(slug)}/snippets`, {
      method: "POST",
      body,
    }),
  patch: (
    slug: string,
    id: string,
    body: Partial<{
      title: string;
      shortcut: string | null;
      body: string;
      teamId: string | null;
      isActive: boolean;
    }>,
  ) => api(`${base(slug)}/snippets/${id}`, { method: "PATCH", body }),
  remove: (slug: string, id: string) =>
    api(`${base(slug)}/snippets/${id}`, { method: "DELETE" }),
};
export interface FieldDefinition {
  key: string;
  label: Record<string, string> | string;
  type:
    | "text"
    | "number"
    | "date"
    | "boolean"
    | "select"
    | "phone"
    | "email"
    | "cpf"
    | "cnpj";
  options: string[] | null;
  aiExtractable: boolean;
  aiExtractionHint: string | null;
  required: boolean;
  sort: number;
  isActive: boolean;
}
export const fieldsApi = {
  list: (slug: string, all = false) =>
    api<{ fields: FieldDefinition[] }>(`${base(slug)}/contact-fields`, {
      query: all ? { all: 1 } : {},
    }),
  create: (slug: string, body: Record<string, unknown>) =>
    api<{ field: FieldDefinition }>(`${base(slug)}/contact-fields`, {
      method: "POST",
      body,
    }),
  patch: (slug: string, key: string, body: Record<string, unknown>) =>
    api(`${base(slug)}/contact-fields/${key}`, { method: "PATCH", body }),
};
export const optoutApi = {
  get: (slug: string) =>
    api<{ keywords: string[]; reply: string | null }>(
      `${base(slug)}/settings/optout`,
    ),
  put: (slug: string, body: { keywords: string[]; reply: string | null }) =>
    api(`${base(slug)}/settings/optout`, { method: "PUT", body }),
};
/** How long the flow and the assistant wait for the contact to stop writing. */
export const repliesApi = {
  get: (slug: string) =>
    api<{ waitSeconds: number }>(`${base(slug)}/settings/replies`),
  put: (slug: string, waitSeconds: number) =>
    api<{ waitSeconds: number }>(`${base(slug)}/settings/replies`, {
      method: "PUT",
      body: { waitSeconds },
    }),
};
export const contactsListApi = {
  list: (slug: string, q: { q?: string; limit?: number; before?: string }) =>
    api<{ contacts: ContactListRow[] }>(`${base(slug)}/contacts`, { query: q }),
  create: (
    slug: string,
    body: {
      displayName: string;
      phone: string;
      fields?: Record<string, unknown>;
    },
  ) =>
    api<{ contact: { id: string } }>(`${base(slug)}/contacts`, {
      method: "POST",
      body,
    }),
  import: (slug: string, rows: ImportRow[]) =>
    api<ImportResult>(`${base(slug)}/contacts/import`, {
      method: "POST",
      body: { rows },
    }),
};
export const conversationsApi = {
  start: (
    slug: string,
    body: {
      contactId: string;
      channelId?: string;
      templateId: string;
      parameters: Record<string, unknown>;
      teamId?: string | null;
      goal?: string;
      /** A template with a media header: the file staged for this send. */
      headerUploadId?: string;
      /** Confirmed for a contact who paused active messages (decision 0036). */
      confirmHold?: boolean;
    },
  ) =>
    api<{ conversation: { id: string }; message: { id: string } }>(
      `${base(slug)}/conversations`,
      { method: "POST", body },
    ),
};

/** Fills snippet variables with the people in the conversation. */
export function renderSnippet(
  body: string,
  ctx: { contactName?: string; memberName?: string },
): string {
  const first = (s?: string) => (s ?? "").trim().split(/\s+/)[0] ?? "";
  const vars: Record<string, string> = {
    "contact.name": ctx.contactName ?? "",
    "contact.first_name": first(ctx.contactName),
    "member.name": ctx.memberName ?? "",
    "member.first_name": first(ctx.memberName),
  };
  return body.replace(
    /\{\{\s*([a-z_.]+)\s*\}\}/g,
    (m, k: string) => vars[k] ?? m,
  );
}
